import * as React from "react";
import {
  Controller,
  useFormContext,
  get,
} from "react-hook-form";
import { Label } from "../../../../components/ui/label";
import { Input } from "../../../../components/ui/input";

/* ---------- date helpers (match previous impl) ---------- */
function mmddyyyy(d) {
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${mm}/${dd}/${d.getFullYear()}`;
}
function getYearSpans(today = new Date()) {
  const end1 = new Date(today);
  end1.setFullYear(end1.getFullYear() - 1);
  const end2 = new Date(today);
  end2.setFullYear(end2.getFullYear() - 2);
  const end3 = new Date(today);
  end3.setFullYear(end3.getFullYear() - 3);
  return [
    { start: today, end: end1 },
    { start: end1, end: end2 },
    { start: end2, end: end3 },
  ];
}

/* ---------- UI config ---------- */
const CATEGORIES = [
  {
    key: "autoLiability",
    left: "Auto liability: Loss count (Open or closed)",
    right: "Auto liability: Losses paid (Open or reserved)",
  },
  {
    key: "physicalDamage",
    left: "Physical damage: Loss count (Open or closed)",
    right: "Physical damage: Losses paid (Open or reserved)",
  },
  {
    key: "cargo",
    left: "Cargo: Loss count (Open or closed)",
    right: "Cargo: Losses paid (Open or reserved)",
  },
  {
    key: "tgl",
    left: "Truckers general liability: Loss count (Open or closed)",
    right: "Truckers general liability: Losses paid (Open or reserved)",
  },
];

/* ---------- Field components (use RHF context inside this subform) ---------- */
function LossNumberInput({ name, id, label }) {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const error = get(errors, name);

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <>
            <Input
              id={id}
              type="number"
              min={0}
              placeholder="Enter..."
              value={field.value ?? ""} // allow clearing
              onChange={(e) =>
                e.target.value === ""
                  ? field.onChange(undefined)
                  : field.onChange(Number(e.target.value))
              }
              aria-invalid={!!error}
            />
            {error?.message && (
              <p className="text-xs text-red-600">{error.message}</p>
            )}
          </>
        )}
      />
    </div>
  );
}

function LossRow({ periodIndex, catKey, leftLabel, rightLabel }) {
  const left = `step9.losses.${periodIndex}.${catKey}.count`;
  const right = `step9.losses.${periodIndex}.${catKey}.paid`;
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <LossNumberInput
        name={left}
        id={`${catKey}-count-${periodIndex}`}
        label={leftLabel}
      />
      <LossNumberInput
        name={right}
        id={`${catKey}-paid-${periodIndex}`}
        label={rightLabel}
      />
    </div>
  );
}

/* ---------- MAIN SUBFORM ---------- */
export function LossesForm() {
  const spans = React.useMemo(() => getYearSpans(new Date()), []);

  return (
    <div>
      <h3 className="text-2xl font-semibold">Losses</h3>

      {spans.map((s, idx) => (
        <div key={idx} className="space-y-4">
          <div className="text-blue-600 font-semibold text-lg">
            {mmddyyyy(s.start)} - {mmddyyyy(s.end)}
          </div>
          {CATEGORIES.map((c) => (
            <LossRow
              key={c.key}
              periodIndex={idx}
              catKey={c.key}
              leftLabel={c.left}
              rightLabel={c.right}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
